import React, { useState, useEffect } from 'react';
import { Input } from 'tea-component';

interface IHeadcountEditableCellProps {
  initialValue: string | number | null;
  onChange: (value: string) => void;
  type: 'suggestedHC' | 'suggestedValue';
}

/**
 * Check if a string is a valid number format (entire string must be a number)
 * Regex: ^ = start, -? = optional minus, \d+ = digits, \.? = optional decimal, \d* = optional decimal digits, $ = end
 * This prevents cases like '17jm' from being parsed as 17
 */
const isValidNumber = (str: string): boolean => {
  return /^-?\d+\.?\d*$/.test(str);
};

/**
 * Format a number value to 1 decimal place, with special handling for zero
 */
const formatNumber = (value: number): string => {
  return value === 0 ? '0' : value.toFixed(1);
};

/**
 * Editable cell component for Headcount table
 * Supports both Model's Suggested HC (with range values) and Suggested Value fields
 */
const HeadcountEditableCell: React.FC<IHeadcountEditableCellProps> = ({ 
  initialValue, 
  onChange, 
  type 
}) => {
  // Format initial display value based on type
  const formatInitialValue = (val: string | number | null): string => {
    if (val === null || val === undefined || val === '') {
      return type === 'suggestedValue' ? '0' : '';
    }
    
    if (type === 'suggestedValue') {
      // For Suggested Value: show 1 decimal for valid numbers only
      if (typeof val === 'number') {
        return formatNumber(val);
      }
      
      const strVal = String(val);
      if (isValidNumber(strVal)) {
        return formatNumber(parseFloat(strVal));
      }
      // Not a valid number → return as-is (e.g., "26jm", "NA")
      return strVal;
    }
    
    return String(val);
  };

  const [value, setValue] = useState(formatInitialValue(initialValue));
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [isFirstLoad]);

  // Update internal state when initialValue changes (e.g., from auto-save restore or data refresh)
  useEffect(() => {
    setValue(formatInitialValue(initialValue));
  }, [initialValue, type]);
  
  const handleChange = (newValue: string) => {
    // Update local state immediately for responsive UI
    setValue(newValue);
  };

  const handleBlur = () => {
    if (isFirstLoad) return;

    const trimmedValue = value.trim();
    
    if (type === 'suggestedHC') {
      // Model's Suggested HC: support any text including range format (e.g., "3-5*")
      // Empty string remains empty
      onChange(trimmedValue);
    } else {
      // Suggested Value: special handling
      const lowerValue = trimmedValue.toLowerCase();
      
      if (trimmedValue === '' || trimmedValue === '-') {
        // Empty or "-" → send "0" to backend, display will show "0"
        onChange('0');
        setValue('0');
      } else if (lowerValue === 'na' || lowerValue === 'n/a') {
        // "NA" → send "NA" to backend
        onChange('NA');
        setValue('NA');
      } else {
        // Check if it's a valid number (entire string must be a number, not just starting with one)
        if (isValidNumber(trimmedValue)) {
          // Valid number → format to 1 decimal place
          const formatted = formatNumber(parseFloat(trimmedValue));
          onChange(formatted);
          setValue(formatted);
        } else {
          // Not a valid number → keep as-is (like "10jming", "abc")
          onChange(trimmedValue);
        }
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Input
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      style={{ textAlign: 'right', width: '100%' }}
      placeholder={type === 'suggestedValue' ? '0' : ''}
    />
  );
};

export default HeadcountEditableCell;
